import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiSearch, FiActivity, FiZap, FiLayers, FiDatabase, 
  FiTrendingUp, FiCheckCircle, FiAlertTriangle, FiMinusCircle,
  FiTrash2, FiEdit3, FiPackage, FiBox, FiCheck
} = FiIcons;

const TABLES = {
  MATERIALS: 'rox_materials_1740635000',
  ITEMS: 'rox_items_1740635000',
  SAVED_SMELTING: 'rox_smelting_calc_1740660000'
};

export default function SmeltingCalc() {
  const [staminaValue, setStaminaValue] = useState(1);
  const [items, setItems] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [savedCalcs, setSavedCalcs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCalcId, setActiveCalcId] = useState(null);

  const [excludeMat1, setExcludeMat1] = useState(false);
  const [excludeMat2, setExcludeMat2] = useState(false);

  useEffect(() => {
    fetchData();
    const savedStamina = localStorage.getItem('rox_stamina_value');
    if (savedStamina) setStaminaValue(parseFloat(savedStamina));
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [itemsRes, matsRes, savedRes] = await Promise.all([
        supabase.from(TABLES.ITEMS).select('*').order('item_name'),
        supabase.from(TABLES.MATERIALS).select('*'),
        supabase.from(TABLES.SAVED_SMELTING).select('*').order('created_at', { ascending: false })
      ]);

      if (itemsRes.data) {
        const dualMaterialItems = itemsRes.data.filter(item => 
          item.material_1 && item.material_1.trim() !== '' && 
          item.material_2 && item.material_2.trim() !== ''
        );
        setItems(dualMaterialItems);
      }
      
      if (matsRes.data) setMaterials(matsRes.data);
      if (savedRes.data) setSavedCalcs(savedRes.data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStaminaChange = (val) => {
    const v = parseFloat(val) || 0;
    setStaminaValue(v);
    localStorage.setItem('rox_stamina_value', v.toString());
  };

  const getMaterialData = (name) => materials.find(m => m.item_name === name) || { e_cost: 0, stamina_cost: 0 };

  const calculation = useMemo(() => {
    if (!selectedItem) return null;

    const mat1 = getMaterialData(selectedItem.material_1);
    const mat2 = getMaterialData(selectedItem.material_2);

    const mat1Total = excludeMat1 ? 0 : (mat1.e_cost * (selectedItem.qty_1 || 0)) + (mat1.stamina_cost * staminaValue * (selectedItem.qty_1 || 0));
    const mat2Total = excludeMat2 ? 0 : (mat2.e_cost * (selectedItem.qty_2 || 0)) + (mat2.stamina_cost * staminaValue * (selectedItem.qty_2 || 0));

    const totalMaterialCost = mat1Total + mat2Total;
    const itemMarketCost = selectedItem.e_cost || 0;

    let status = 'buy';
    let diff = itemMarketCost - totalMaterialCost;
    
    if (totalMaterialCost < itemMarketCost * 0.95) status = 'smelt';
    else if (Math.abs(diff) < itemMarketCost * 0.05) status = 'breakeven';

    return {
      mat1, mat2,
      mat1Total, mat2Total,
      totalMaterialCost,
      itemMarketCost,
      status,
      diff: Math.abs(diff)
    };
  }, [selectedItem, staminaValue, materials, excludeMat1, excludeMat2]);

  const saveCalculation = async () => {
    if (!selectedItem || !calculation) return;
    const data = {
      item_name: selectedItem.item_name,
      stamina_value_at_save: staminaValue,
      total_material_cost: calculation.totalMaterialCost,
      item_market_cost: calculation.itemMarketCost,
      result_status: calculation.status,
      exclude_mat_1: excludeMat1,
      exclude_mat_2: excludeMat2
    };

    const { error } = activeCalcId 
      ? await supabase.from(TABLES.SAVED_SMELTING).update(data).eq('id', activeCalcId)
      : await supabase.from(TABLES.SAVED_SMELTING).insert([data]);

    if (!error) fetchData();
    else alert('Error saving: ' + error.message);
  };

  const handleDeleteHistory = async (id) => {
    if (window.confirm('Delete this calculation from history?')) {
      const { error } = await supabase
        .from(TABLES.SAVED_SMELTING)
        .delete()
        .eq('id', id);
      
      if (error) {
        alert('Error deleting: ' + error.message);
      } else {
        if (activeCalcId === id) setActiveCalcId(null);
        fetchData();
      }
    }
  };

  const getStatusConfig = (status) => {
    switch(status) {
      case 'smelt': return { label: 'Buy Required Material', color: 'bg-green-600', icon: FiCheckCircle, desc: 'Crafting this item is significantly cheaper than buying.' };
      case 'buy': return { label: 'Buy Instead', color: 'bg-red-600', icon: FiAlertTriangle, desc: 'Market price is lower than the cost of materials + stamina.' };
      case 'breakeven': return { label: 'Break Even', color: 'bg-amber-500', icon: FiMinusCircle, desc: 'Cost is roughly the same. Choose based on convenience.' };
      default: return { label: 'Unknown', color: 'bg-gray-400', icon: FiActivity, desc: 'No analysis available.' };
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-2">
          <h1 className="text-4xl font-semibold tracking-tight flex items-center gap-3">Smelting Calc</h1>
          <p className="text-gray-500">Compare crafting costs with exchange market values.</p>
        </div>
        <div className="bg-amber-50/50 p-4 rounded-3xl border border-amber-100 flex items-center gap-4">
          <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-white">
            <SafeIcon icon={FiZap} />
          </div>
          <div>
            <label className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Stamina Crystal Value</label>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold">1 Stamina =</span>
              <input type="number" value={staminaValue} onChange={(e) => handleStaminaChange(e.target.value)} className="w-20 bg-transparent border-b border-amber-200 font-mono font-bold text-amber-700 outline-none text-center" />
              <span className="text-sm font-bold text-amber-600">Crystals</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-xl space-y-8">
            <div className="relative">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1 tracking-widest mb-2 block">
                Search Recipe Item (Items with 2 Materials Only)
              </label>
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  value={searchQuery} 
                  onChange={(e) => setSearchQuery(e.target.value)} 
                  placeholder="Type item name..." 
                  className="w-full pl-11 pr-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:bg-white transition-all shadow-inner font-medium" 
                />
              </div>
              
              <AnimatePresence>
                {searchQuery && (
                  <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl max-h-60 overflow-y-auto custom-scrollbar">
                    {items.filter(i => i.item_name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 10).map((item, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => { 
                          setSelectedItem(item); 
                          setSearchQuery(''); 
                          setActiveCalcId(null); 
                          setExcludeMat1(false); 
                          setExcludeMat2(false); 
                        }} 
                        className="w-full px-5 py-3 text-left hover:bg-blue-50 flex justify-between items-center border-b border-gray-50 last:border-0 transition-colors"
                      >
                        <div>
                          <p className="font-bold text-gray-900">{item.item_name}</p>
                          <p className="text-[10px] text-gray-400 uppercase font-black">{item.category}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-mono font-bold text-blue-600">{item.e_cost} C</p>
                          <p className="text-[8px] text-gray-400 font-bold uppercase">Recipe: {item.material_1} + {item.material_2}</p>
                        </div>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {selectedItem && calculation ? (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className={`p-6 rounded-3xl border transition-all duration-300 ${excludeMat1 ? 'bg-gray-100/50 grayscale border-gray-200' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Material 1</p>
                        <h4 className="font-bold text-lg leading-tight">{selectedItem.material_1}</h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">Quantity: x{selectedItem.qty_1}</p>
                      </div>
                      <button 
                        onClick={() => setExcludeMat1(!excludeMat1)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all ${excludeMat1 ? 'bg-black text-white' : 'bg-white text-gray-400 border border-gray-200 hover:border-gray-300'}`}
                      >
                        <SafeIcon icon={excludeMat1 ? FiCheck : FiBox} />
                        {excludeMat1 ? 'Owned' : 'Own?'}
                      </button>
                    </div>
                    
                    <div className={`space-y-2 pt-2 border-t border-gray-200/50 ${excludeMat1 ? 'opacity-30' : ''}`}>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 font-medium">Exchange Cost</span>
                        <span className="font-mono font-bold">{calculation.mat1.e_cost} C</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 font-medium">Stamina Cost ({calculation.mat1.stamina_cost} pt)</span>
                        <span className="font-mono font-bold">{(calculation.mat1.stamina_cost * staminaValue).toLocaleString()} C</span>
                      </div>
                      <div className="flex justify-between text-sm font-black pt-2 border-t border-dashed border-gray-300">
                        <span>Line Total</span>
                        <span className="text-blue-700">{excludeMat1 ? '0' : calculation.mat1Total.toLocaleString()} C</span>
                      </div>
                    </div>
                  </div>

                  <div className={`p-6 rounded-3xl border transition-all duration-300 ${excludeMat2 ? 'bg-gray-100/50 grayscale border-gray-200' : 'bg-gray-50 border-gray-100 shadow-sm'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <p className="text-[10px] font-black text-purple-600 uppercase tracking-widest">Material 2</p>
                        <h4 className="font-bold text-lg leading-tight">{selectedItem.material_2}</h4>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mt-0.5">Quantity: x{selectedItem.qty_2}</p>
                      </div>
                      <button 
                        onClick={() => setExcludeMat2(!excludeMat2)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase transition-all ${excludeMat2 ? 'bg-black text-white' : 'bg-white text-gray-400 border border-gray-200 hover:border-gray-300'}`}
                      >
                        <SafeIcon icon={excludeMat2 ? FiCheck : FiBox} />
                        {excludeMat2 ? 'Owned' : 'Own?'}
                      </button>
                    </div>
                    
                    <div className={`space-y-2 pt-2 border-t border-gray-200/50 ${excludeMat2 ? 'opacity-30' : ''}`}>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 font-medium">Exchange Cost</span>
                        <span className="font-mono font-bold">{calculation.mat2.e_cost} C</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 font-medium">Stamina Cost ({calculation.mat2.stamina_cost} pt)</span>
                        <span className="font-mono font-bold">{(calculation.mat2.stamina_cost * staminaValue).toLocaleString()} C</span>
                      </div>
                      <div className="flex justify-between text-sm font-black pt-2 border-t border-dashed border-gray-300">
                        <span>Line Total</span>
                        <span className="text-purple-700">{excludeMat2 ? '0' : calculation.mat2Total.toLocaleString()} C</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-8 border-t border-gray-100">
                  <div className="grid md:grid-cols-3 gap-4 mb-8">
                    <div className="p-5 bg-gray-50 rounded-3xl border border-gray-100 text-center">
                      <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Total Material Cost</p>
                      <p className="text-2xl font-mono font-black">{calculation.totalMaterialCost.toLocaleString()} C</p>
                    </div>
                    <div className="p-5 bg-blue-50 rounded-3xl border border-blue-100 text-center">
                      <p className="text-[10px] font-black text-blue-600 uppercase mb-1">Exchange Market Price</p>
                      <p className="text-2xl font-mono font-black text-blue-700">{calculation.itemMarketCost.toLocaleString()} C</p>
                    </div>
                    <div className={`p-5 rounded-3xl border text-center ${calculation.status === 'smelt' ? 'bg-green-50 border-green-100' : 'bg-red-50 border-red-100'}`}>
                      <p className="text-[10px] font-black text-gray-500 uppercase mb-1">Difference</p>
                      <p className={`text-2xl font-mono font-black ${calculation.status === 'smelt' ? 'text-green-600' : 'text-red-600'}`}>{calculation.diff.toLocaleString()} C</p>
                    </div>
                  </div>

                  <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`p-6 rounded-[2rem] border flex items-center justify-between text-white shadow-xl transition-all duration-500 ${getStatusConfig(calculation.status).color}`}>
                    <div className="flex items-center gap-5">
                      <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
                        <SafeIcon icon={getStatusConfig(calculation.status).icon} className="text-3xl" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-black uppercase tracking-tight leading-none mb-1">
                          {getStatusConfig(calculation.status).label}
                        </h3>
                        <p className="text-sm opacity-95 font-medium">{getStatusConfig(calculation.status).desc}</p>
                      </div>
                    </div>
                  </motion.div>

                  <div className="flex justify-end gap-3 mt-8">
                    <button onClick={() => { setSelectedItem(null); setActiveCalcId(null); setExcludeMat1(false); setExcludeMat2(false); }} className="px-6 py-3 rounded-xl text-sm font-bold text-gray-400 hover:text-black transition-colors">Clear Selection</button>
                    <button onClick={saveCalculation} className="px-10 py-3 bg-black text-white rounded-xl text-sm font-bold shadow-xl shadow-black/10 transition-all hover:scale-[1.05] active:scale-[0.95]">
                      {activeCalcId ? 'Update Record' : 'Save Analysis'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-24 border-2 border-dashed border-gray-100 rounded-[2.5rem] flex flex-col items-center justify-center text-gray-300">
                <SafeIcon icon={FiActivity} className="text-5xl opacity-20 mb-4" />
                <p className="text-lg font-bold uppercase tracking-widest">Select an item to see the breakdown</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between px-2">
            <h2 className="text-xl font-bold flex items-center gap-3">
              <SafeIcon icon={FiTrendingUp} className="text-gray-400" /> Analysis History
            </h2>
            <span className="text-[10px] font-black bg-gray-100 px-3 py-1.5 rounded-full text-gray-500 uppercase tracking-widest">{savedCalcs.length}</span>
          </div>
          
          <div className="space-y-4 max-h-[800px] overflow-y-auto no-scrollbar pb-10">
            {savedCalcs.map(calc => {
              const config = getStatusConfig(calc.result_status);
              const anyExcluded = calc.exclude_mat_1 || calc.exclude_mat_2;
              return (
                <div key={calc.id} className={`bg-white p-5 rounded-3xl border shadow-sm hover:shadow-md transition-all group ${activeCalcId === calc.id ? 'border-blue-500 ring-2 ring-blue-50' : 'border-gray-100'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 leading-tight group-hover:text-blue-600 transition-colors">{calc.item_name}</h4>
                      <p className="text-[9px] font-black text-gray-400 uppercase mt-1">Stamina: {calc.stamina_value_at_save} C</p>
                      {anyExcluded && <p className="text-[8px] font-black text-blue-500 uppercase mt-0.5 tracking-tighter">Adjusted for owned mats</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => { 
                        const item = items.find(i => i.item_name === calc.item_name);
                        if (item) { 
                          setSelectedItem(item); 
                          setActiveCalcId(calc.id); 
                          setStaminaValue(calc.stamina_value_at_save);
                          setExcludeMat1(calc.exclude_mat_1 || false);
                          setExcludeMat2(calc.exclude_mat_2 || false);
                        }
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }} className="p-1.5 text-gray-300 hover:text-black transition-colors"><SafeIcon icon={FiEdit3} /></button>
                      <button onClick={() => handleDeleteHistory(calc.id)} className="p-1.5 text-gray-300 hover:text-red-500 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                    <div className="flex items-center gap-2">
                       <div className={`w-2 h-2 rounded-full ${config?.color || 'bg-gray-200'}`} />
                       <span className="text-[10px] font-black uppercase tracking-widest text-gray-500">{config?.label || 'Unknown'}</span>
                    </div>
                    <span className="font-mono text-xs font-bold text-gray-900">{(calc.total_material_cost || 0).toLocaleString()} C</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}